import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";

const GoogleAdsCalculator = () => {
    const [targetSales, setTargetSales] = useState('');
     useTheme();
    const [valuePerSale, setValuePerSale] = useState('');
    const [avgCpc, setAvgCpc] = useState('');
    const [convRate, setConvRate] = useState('');
    const [investmentOutput, setInvestmentOutput] = useState('');
    const [adjustedBudget, setAdjustedBudget] = useState('');
    const [adjustedOutput, setAdjustedOutput] = useState('');
    const [showAdjustment, setShowAdjustment] = useState(false);

    const calculateInvestment = () => {
        if (!targetSales || !valuePerSale || !avgCpc || !convRate) {
            setInvestmentOutput('Please fill in all fields.');
            return;
        }

        const websiteVisitsNeeded = Math.ceil(targetSales / (convRate / 100));
        const investmentNeeded = avgCpc * websiteVisitsNeeded;
        const estimatedReturn = valuePerSale * targetSales;
        const roi = (estimatedReturn / investmentNeeded) * 100;

        setInvestmentOutput(`
            To reach ${targetSales} sales per month, you need to invest approximately $${investmentNeeded.toFixed(2)}.
            You will need around ${websiteVisitsNeeded} website visits to generate ${targetSales} sales.
            The estimated return from these additional sales is $${estimatedReturn.toFixed(2)}.
            Expected ROI: ${roi.toFixed(2)}%.
        `);
        setShowAdjustment(true);
    };

    const adjustInvestment = () => {
        const budget = parseFloat(adjustedBudget);
        if (isNaN(budget)) {
            setAdjustedOutput('Please enter a valid budget amount.');
            return;
        }
        
        if (!avgCpc || !valuePerSale || !convRate) {
            setAdjustedOutput('Please fill in all fields.');
            return;
        }

        const websiteVisitsPossible = Math.floor(budget / avgCpc);
        const adjustedSales = Math.floor((websiteVisitsPossible * convRate) / 100);
        const estimatedReturn = valuePerSale * adjustedSales;
        const roi = (estimatedReturn / budget) * 100;

        setAdjustedOutput(`
            With an adjusted budget of $${budget.toFixed(2)}, you can get approximately ${adjustedSales} sales.
            You will need around ${websiteVisitsPossible} website visits to generate ${adjustedSales} sales.
            The estimated return from these adjusted sales is $${estimatedReturn.toFixed(2)}.
            Expected ROI: ${roi.toFixed(2)}%.
        `);
    };

    return (
        <>
            <HeaderSection toolId="googleAdsCalculator" />
            
            <motion.main 
                className="tool-w-full lg:px-4 dark:bg-gray-800"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                className="mr-3"
                            />
                            Google Ads Calculator
                        </h1>
                        <p className="opacity-90">
                            Calculate your potential investment and returns from Google Ads
                        </p>
                    </div>

                    <motion.section 
                        className="space-y-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        <form className="space-y-5 p-6">
                            <motion.div 
                                className="grid grid-cols-1 md:grid-cols-2 gap-5"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: {
                                        opacity: 1,
                                        transition: {
                                            staggerChildren: 0.1
                                        }
                                    }
                                }}
                                initial="hidden"
                                animate="visible"
                            >
                                <motion.fieldset 
                                    className="space-y-2"
                                    variants={{
                                        hidden: { y: 10, opacity: 0 },
                                        visible: { y: 0, opacity: 1 }
                                    }}
                                >
                                    <label htmlFor="target-sales" className="block text-gray-700 dark:text-gray-200 font-medium">Target Sales per Month</label>
                                    <input
                                        type="number"
                                        id="target-sales"
                                        value={targetSales}
                                        onChange={(e) => setTargetSales(e.target.value)}
                                        placeholder="e.g. 100"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        aria-label="Enter your target sales per month"
                                    />
                                </motion.fieldset>

                                <motion.fieldset 
                                    className="space-y-2"
                                    variants={{
                                        hidden: { y: 10, opacity: 0 },
                                        visible: { y: 0, opacity: 1 }
                                    }}
                                >
                                    <label htmlFor="value-per-sale" className="block text-gray-700 dark:text-gray-200 font-medium">Value per Sale ($)</label>
                                    <input
                                        type="number"
                                        id="value-per-sale"
                                        value={valuePerSale}
                                        onChange={(e) => setValuePerSale(e.target.value)}
                                        placeholder="e.g. 50"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        aria-label="Enter the value of each sale in dollars"
                                    />
                                </motion.fieldset>

                                <motion.fieldset 
                                    className="space-y-2"
                                    variants={{
                                        hidden: { y: 10, opacity: 0 },
                                        visible: { y: 0, opacity: 1 }
                                    }}
                                >
                                    <label htmlFor="avg-cpc" className="block text-gray-700 dark:text-gray-200 font-medium">Avg. Cost Per Click ($)</label>
                                    <input
                                        type="number"
                                        id="avg-cpc"
                                        value={avgCpc}
                                        onChange={(e) => setAvgCpc(e.target.value)}
                                        placeholder="e.g. 1.25"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        aria-label="Enter average cost per click in dollars"
                                        step="0.01"
                                    />
                                </motion.fieldset>

                                <motion.fieldset 
                                    className="space-y-2"
                                    variants={{
                                        hidden: { y: 10, opacity: 0 },
                                        visible: { y: 0, opacity: 1 }
                                    }}
                                >
                                    <label htmlFor="conv-rate" className="block text-gray-700 dark:text-gray-200 font-medium">Conversion Rate (%)</label>
                                    <input
                                        type="number"
                                        id="conv-rate"
                                        value={convRate}
                                        onChange={(e) => setConvRate(e.target.value)}
                                        placeholder="e.g. 5"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        aria-label="Enter estimated conversion rate percentage"
                                        min="0"
                                        max="100"
                                    />
                                </motion.fieldset>
                            </motion.div>

                            <motion.button
                                type="button"
                                onClick={calculateInvestment}
                                className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                                aria-label="Calculate investment required"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Calculate Investment
                            </motion.button>
                        </form>

                        {investmentOutput && (
                            <motion.section 
                                className="m-5 p-5 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20"
                                aria-live="polite"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-3">Calculation Results</h3>
                                {investmentOutput.split('\n').map((line, i) => (
                                    <p key={i} className="text-gray-700 dark:text-gray-200 mb-2 last:mb-0">{line}</p>
                                ))}
                            </motion.section>
                        )}

                        {showAdjustment && (
                            <motion.section 
                                className="space-y-4 mb-3 m-5 p-5 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <h3 className="text-lg font-semibold text-green-800 mb-3 dark:text-green-300">Adjust Your Budget</h3>
                                <form className="space-y-3 mb-3">
                                    <label htmlFor="adjusted-budget" className="block text-gray-700 dark:text-gray-200 font-medium">New Budget Amount ($)</label>
                                    <div className="flex space-x-3 mb-3"> 
                                        <input
                                            type="number"
                                            id="adjusted-budget"
                                            value={adjustedBudget}
                                            onChange={(e) => setAdjustedBudget(e.target.value)}
                                            onBlur={adjustInvestment}
                                            placeholder="Enter your adjusted budget"
                                            className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                            aria-label="Enter adjusted budget amount in dollars"
                                            step="0.01"
                                        />
                                        <button
                                            type="button"
                                            onClick={adjustInvestment}
                                            className="bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300"
                                        >
                                            Adjust
                                        </button>
                                    </div>
                                </form>

                                {adjustedOutput && (
                                    <article className="p-4  bg-white dark:bg-gray-800 rounded-md border border-green-200 dark:border-green-800">
                                        <h4 className="font-medium text-green-700 dark:text-green-300 mb-2">Adjusted Results:</h4>
                                        {adjustedOutput.split('\n').map((line, i) => (
                                            <p key={i} className="text-gray-700 dark:text-gray-200 mb-2 last:mb-0">{line}</p>
                                        ))}
                                    </article>
                                )}
                            </motion.section>
                        )}
                    </motion.section>
                </div>
            </motion.main>
            
            <BottomSection toolId="googleAdsCalculator" />
        </>
    );
};

export default GoogleAdsCalculator;